import { useCallback, useRef, useState, type DragEvent } from 'react';
import { UploadIcon } from '../components/icons';
import { Banner, Button, cx } from '../components/ui';
import { bytes } from '../lib/format';
import type { CompleteVideoResponse } from '../lib/types';
import { PART_SIZE, StreamingUpload, type UploadState, type UploadTransport } from './uploader';

/**
 * Uploading a file that already exists.
 *
 * On /r/:token this is not a fallback: it is how iPhone screen recordings
 * arrive, and it sits next to the record button as an equal. On /new it is
 * what we offer a browser that cannot capture a screen.
 *
 * Audio files go through the identical path. The API reads the content type,
 * derives a `kind` of 'audio' from it, and picks the audio transcode template;
 * nothing here needs to know the difference beyond which types to accept and
 * which element to ask for the duration.
 *
 * It reuses the recorder's multipart uploader wholesale: slice the file into
 * parts, push them through the same queue, get the same retries.
 */

const ACCEPTED_EXTENSIONS = [
  '.mp4',
  '.mov',
  '.webm',
  '.m4v',
  '.m4a',
  '.mp3',
  '.wav',
  '.ogg',
  '.oga',
  '.weba',
];

/** Exactly the content types slate-api accepts. Anything else is a 400. */
const ACCEPTED_CONTENT_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'audio/webm',
  'audio/ogg',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
] as const;

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  weba: 'audio/webm',
};

/**
 * Browsers disagree about audio types more than video ones: the same .wav is
 * `audio/wav` in Chrome and `audio/x-wav` in Firefox, and Windows sometimes
 * declares `audio/mpeg3`. So a declared type is only trusted when it is one
 * the API knows; otherwise the extension decides.
 */
export function contentTypeFor(file: File): string | null {
  const declared = file.type.toLowerCase();
  if ((ACCEPTED_CONTENT_TYPES as readonly string[]).includes(declared)) return declared;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return CONTENT_TYPE_BY_EXTENSION[extension] ?? null;
}

/** Metadata duration, best effort: a wrong guess is overwritten by transcode. */
async function readDuration(file: File, contentType: string): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const media: HTMLMediaElement = contentType.startsWith('audio/')
      ? document.createElement('audio')
      : document.createElement('video');
    const done = (value: number | undefined) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    media.preload = 'metadata';
    media.onloadedmetadata = () =>
      done(Number.isFinite(media.duration) && media.duration > 0 ? media.duration : undefined);
    media.onerror = () => done(undefined);
    window.setTimeout(() => done(undefined), 5000);
    media.src = url;
  });
}

export interface FileUploadProps {
  transport: UploadTransport;
  makeTitle: (file: File) => string;
  maxBytes?: number;
  onUploaded: (result: CompleteVideoResponse) => void;
  compact?: boolean;
  /** Sent to the API as the video's source; 'upload' is the paid feature. */
  source?: 'upload';
}

export function FileUpload({
  transport,
  makeTitle,
  maxBytes,
  onUploaded,
  compact = false,
  source,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<UploadState | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File) => {
      setError(null);

      const contentType = contentTypeFor(file);
      if (!contentType) {
        setError(
          `Slate takes MP4, MOV and WebM video, and M4A, MP3, WAV and OGG audio. ` +
            `"${file.name}" does not look like any of those.`,
        );
        return;
      }
      if (maxBytes && file.size > maxBytes) {
        setError(`That file is ${bytes(file.size)}. The limit is ${bytes(maxBytes)}.`);
        return;
      }
      if (file.size === 0) {
        setError('That file is empty.');
        return;
      }

      setBusy(true);
      setFileName(file.name);

      const uploader = new StreamingUpload(transport, setProgress);
      try {
        const duration = await readDuration(file, contentType);
        void uploader.start({
          contentType,
          title: makeTitle(file),
          partCount: Math.min(100, Math.max(1, Math.ceil(file.size / PART_SIZE))),
          ...(source ? { source } : {}),
        });

        // Slice rather than read: the browser keeps the bytes on disk and the
        // uploader only ever holds one part in memory at a time.
        for (let offset = 0; offset < file.size; offset += PART_SIZE) {
          uploader.push(file.slice(offset, Math.min(offset + PART_SIZE, file.size)));
        }

        const result = await uploader.finish({
          ...(duration !== undefined ? { durationSec: duration } : {}),
        });
        onUploaded(result);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'The upload failed.');
      } finally {
        setBusy(false);
      }
    },
    [makeTitle, maxBytes, onUploaded, source, transport],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files.item(0);
    if (file) void upload(file);
  };

  const percent =
    progress && progress.capturedBytes > 0
      ? Math.min(100, Math.round((progress.uploadedBytes / progress.capturedBytes) * 100))
      : 0;

  return (
    <div className="space-y-3">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cx(
          'rounded-md border border-dashed px-5 text-center transition-colors duration-120',
          compact ? 'py-6' : 'py-10',
          dragging ? 'border-signal bg-signal/[0.06]' : 'border-hairline bg-slate-900',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={`${ACCEPTED_CONTENT_TYPES.join(',')},${ACCEPTED_EXTENSIONS.join(',')}`}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.item(0);
            if (file) void upload(file);
            event.target.value = '';
          }}
        />

        {busy ? (
          <div className="space-y-2">
            <p className="truncate text-body text-chalk">{fileName}</p>
            <div className="mx-auto h-1 w-full max-w-sm overflow-hidden rounded-full bg-slate-800">
              <div
                className="h-full bg-signal transition-[width] duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="tnum text-caption text-chalk-faint">
              {percent}% uploaded{progress ? ` · ${bytes(progress.uploadedBytes)}` : ''}
            </p>
          </div>
        ) : (
          <>
            <UploadIcon size={22} className="mx-auto mb-3 text-chalk-faint" />
            <p className="text-body text-chalk">Drop a video or audio file here</p>
            <p className="mx-auto mt-1 max-w-sm text-caption text-chalk-faint">
              MP4, MOV, WebM, M4A, MP3, WAV or OGG{maxBytes ? ` up to ${bytes(maxBytes)}` : ''}. A
              screen recording straight off an iPhone works fine, and so does a voice memo.
            </p>
            <Button className="mt-4" onClick={() => inputRef.current?.click()}>
              Choose a file
            </Button>
          </>
        )}
      </div>

      {error ? <Banner tone="danger" title="That did not upload">{error}</Banner> : null}
    </div>
  );
}
